// NotificationsList.jsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Badge,
  Button,
  Divider,
  CircularProgress,
} from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import NotificationsIcon from "../../Assets/Svg/Notfications.svg";
import { useNotifications } from "../../Context/notificationContext";
import pusher from "../../tools/pusher";
import { Helper } from "../../tools/Helper";
import { api_Routes } from "../../api_Routes";
import CloseIcon from "@mui/icons-material/Close";
import DoneAllIcon from "@mui/icons-material/DoneAll";

const NotificationsList = ({
  showNotifications,
  toggleNotifications,
  handleClickAway,
  profileData,
}) => {
  const { state, dispatch } = useNotifications();
  const countUnreadNotifications = useCallback(
    (notifs) => notifs.filter((n) => !n.is_read).length,
    []
  );
  const [notifications, setNotificationsState] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const id = profileData ? profileData.id : 0;

  const [isMarkingAll, setIsMarkingAll] = useState(false);

  const getNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const { response } = await Helper.Get({
        url: `${api_Routes.Header.getMyNotifications}`,
        hasToken: true,
      });
      if (response && Array.isArray(response)) {
        setNotificationsState(response);
        const unreadCount = countUnreadNotifications(response);
        dispatch({ type: "SET_NOTIFICATIONS", payload: unreadCount });
      } else {
        console.error("Invalid response format:", response);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, countUnreadNotifications]);

  // Helper to get the correct link from notification data
  const getNotificationLink = (noti) =>
    noti?.description?.link || noti?.message?.description?.link || "#";

  useEffect(() => {
    if (!profileData || !profileData.id) return;
    getNotifications();

    const handleNotification = (data) => {
      try {
        const notificationData =
          typeof data === "string" ? { message: data } : data;

        const newNoti = {
          id: notificationData.id || Date.now(),
          message: notificationData.message || "New notification",
          is_read: false,
          formatted_date: notificationData.date || new Date().toISOString(),
        };

        setNotificationsState((prev) => {
          const updated = [newNoti, ...prev];
          dispatch({
            type: "SET_NOTIFICATIONS",
            payload: countUnreadNotifications(updated),
          });
          return updated;
        });
        dispatch({ type: "ADD_NOTIFICATION" });
      } catch (error) {
        console.error("Error processing notification:", error);
      }
    };

    const channel = pusher.subscribe(`user-notification.${id}`);
    channel.bind("UserNotification", handleNotification);

    return () => {
      channel.unbind("UserNotification", handleNotification);
      pusher.unsubscribe(`user-notification.${id}`);
    };
  }, [dispatch, countUnreadNotifications, profileData, id]);

  // Clear notifications on mount if needed
  useEffect(() => {
    dispatch({ type: "CLEAR_NOTIFICATIONS" });
  }, [dispatch]);

  const handleClickNoti = async (noti) => {
    if (noti.is_read) {
      window.location.href = getNotificationLink(noti);
    } else {
      try {
        const { response } = await Helper.Get({
          url: `${api_Routes.Header.ReadNotification}/${noti.id}`,
          hasToken: true,
        });
        if (response) {
          const updated = notifications.map((n) =>
            n.id === noti.id ? { ...n, is_read: true } : n
          );
          setNotificationsState(updated);
          dispatch({
            type: "SET_NOTIFICATIONS",
            payload: countUnreadNotifications(updated),
          });
          // Navigate to the notification link
          window.location.href = getNotificationLink(noti);
        }
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    if (countUnreadNotifications(notifications) === 0) return;

    setIsMarkingAll(true);
    try {
      const { response } = await Helper.Get({
        url: `${api_Routes.Header.ReadAllNotification}`,
        hasToken: true,
      });

      if (response) {
        const updated = notifications.map((n) => ({ ...n, is_read: true }));
        setNotificationsState(updated);
        dispatch({ type: "SET_NOTIFICATIONS", payload: 0 });
      }
    } catch (error) {
      console.error("Error marking all as read:", error);
    } finally {
      setIsMarkingAll(false);
    }
  };

  const NotificationItem = ({ notification }) => (
    <ListItem
      key={notification.id}
      sx={{
        mb: 1,
        backgroundColor: notification.is_read ? "#f8f9fa" : "#e3f2fd",
        borderRadius: "8px",
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateX(4px)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        },
      }}
      onClick={() => handleClickNoti(notification)}
      button
    >
      <Box sx={{ position: "relative", width: "100%" }}>
        {!notification.is_read && (
          <Box
            sx={{
              position: "absolute",
              left: -8,
              top: "50%",
              transform: "translateY(-50%)",
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: "primary.main",
            }}
          />
        )}
        <ListItemText
          primary={
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {notification?.description?.text ||
                notification?.message?.description?.text ||
                notification.message}
            </Typography>
          }
          secondary={
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {new Date(notification.formatted_date).toLocaleString()}
            </Typography>
          }
          sx={{
            ml: 1.5,
            "& .MuiListItemText-primary": {
              fontSize: "0.875rem",
              lineHeight: 1.3,
            },
          }}
        />
      </Box>
    </ListItem>
  );

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box sx={{ position: "relative" }}>
        <Badge
          badgeContent={state.newNotifications}
          color="error"
          overlap="circular"
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <IconButton
            onClick={toggleNotifications}
            sx={{
              width: "32px",
              height: "32px",
              padding: 0,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <img
              src={NotificationsIcon}
              alt="Notifications"
              style={{ width: "24px", height: "24px" }}
            />
          </IconButton>
        </Badge>

        {showNotifications && (
          <Box
            sx={{
              position: "absolute",
              top: "50px",
              right: { xs: 0, sm: "-70px" },
              left: { xs: -200 },
              width: "360px",
              maxHeight: "400px",
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.paper",
              boxShadow: 3,
              borderRadius: "12px",
              overflow: "hidden",
              zIndex: 99999,
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 2,
                bgcolor: "primary.main",
                color: "primary.contrastText",
              }}
            >
              <Typography variant="h6">Notifications</Typography>
              <IconButton
                onClick={toggleNotifications}
                size="small"
                sx={{ color: "inherit" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Notification List */}
            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              ) : notifications.length > 0 ? (
                <List sx={{ p: 1 }}>
                  {notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                    />
                  ))}
                </List>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No new notifications
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Footer */}
            <Box sx={{ p: 1.5, borderTop: 1, borderColor: "divider" }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                size="small"
                onClick={handleMarkAllAsRead}
                disabled={
                  countUnreadNotifications(notifications) === 0 || isMarkingAll
                }
                startIcon={<DoneAllIcon fontSize="small" />}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  borderRadius: "6px",
                  "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
                }}
              >
                {isMarkingAll ? "Marking All..." : "Mark All as Read"}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default NotificationsList;
